import { Link } from "react-router-dom";
import { ArrowRight, Home, Building2, Wrench, FileCheck, Shield, Clock, IndianRupee, Headphones, Sun, Zap, Award, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteLayout from "@/components/SiteLayout";
import ScrollReveal from "@/components/ScrollReveal";
const heroImage = "/hero-solar.jpg";
import gallery2 from "@/assets/gallery-2.jpg";

const stats = [
  { icon: Home, value: "50+", label: "Rooftops Powered" },
  { icon: Zap, value: "500kW+", label: "Energy Generated" },
  { icon: Sun, value: "300+", label: "Sunny Days/Year" },
  { icon: Award, value: "Growing", label: "Since 2025" },
];

const services = [
  { icon: Home, title: "Residential Solar", desc: "Custom rooftop systems for homes. Save up to 90% on electricity bills." },
  { icon: Building2, title: "Commercial Solar", desc: "Large-scale solutions for offices, factories, and businesses." },
  { icon: Wrench, title: "Maintenance", desc: "Annual maintenance contracts and quick repair services." },
  { icon: FileCheck, title: "Subsidy Help", desc: "Government subsidy & net metering application assistance." },
];

const reasons = [
  { icon: Shield, title: "25-Year Warranty", desc: "Industry-leading warranty on all installations." },
  { icon: IndianRupee, title: "Best Pricing", desc: "Competitive rates with flexible EMI options." },
  { icon: Clock, title: "Fast Installation", desc: "Professional setup within 3-5 working days." },
  { icon: Headphones, title: "24/7 Support", desc: "Dedicated after-sales service and monitoring." },
];

const Index = () => {
  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Solar panels on rooftops in Jaipur" className="w-full h-full object-cover" loading="eager" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-hero" />
        </div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4 opacity-0 animate-fade-up" style={{ animationDelay: "0.1s" }}>
              <MapPin className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Jaipur, Rajasthan</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-secondary-foreground leading-tight mb-5 opacity-0 animate-fade-up" style={{ animationDelay: "0.2s" }}>
              Power Your Home With <span className="text-gradient-solar">Solar Energy</span>
            </h1>
            <p className="text-lg text-secondary-foreground/70 max-w-lg mb-8 opacity-0 animate-fade-up" style={{ animationDelay: "0.4s" }}>
              Trusted rooftop solar panel installation experts. Save up to 90% on your electricity bills with clean, sustainable energy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-up" style={{ animationDelay: "0.6s" }}>
              <Link to="/contact">
                <Button variant="hero" size="lg" className="gap-2">
                  Get Free Quote <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline-light" size="lg">Our Services</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-secondary py-14">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <ScrollReveal key={s.label} delay={i * 100}>
                <div className="text-center">
                  <s.icon className="h-7 w-7 text-primary mx-auto mb-2" />
                  <div className="text-3xl font-heading font-bold text-secondary-foreground">{s.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-14">
              <span className="text-primary font-semibold text-sm uppercase tracking-widest">What We Do</span>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mt-2">Our Solar Services</h2>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <ScrollReveal key={s.title} delay={i * 120}>
                <div className="bg-card rounded-xl p-6 border border-border shadow-card hover:border-primary/40 transition-all group h-full">
                  <div className="w-11 h-11 rounded-lg bg-muted flex items-center justify-center mb-4 group-hover:bg-gradient-solar transition-colors">
                    <s.icon className="h-5 w-5 text-primary group-hover:text-primary-foreground transition-colors" />
                  </div>
                  <h3 className="font-heading font-semibold text-card-foreground mb-1">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={200}>
            <div className="text-center mt-10">
              <Link to="/services">
                <Button variant="solar" className="gap-2">
                  View All Services & Pricing <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ScrollReveal>
                <span className="text-primary font-semibold text-sm uppercase tracking-widest">Why Choose Us</span>
                <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mt-2 mb-8">
                  Jaipur's Trusted Solar Partner
                </h2>
              </ScrollReveal>
              <div className="grid sm:grid-cols-2 gap-5">
                {reasons.map((r, i) => (
                  <ScrollReveal key={r.title} delay={i * 100}>
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center shrink-0">
                        <r.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-foreground text-sm">{r.title}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
            <ScrollReveal direction="right">
              <div className="relative">
                <img src={gallery2} alt="Solar panels with Jaipur skyline" className="rounded-xl shadow-card w-full object-cover aspect-[4/3]" loading="lazy" width={1024} height={766} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Calculator CTA */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="bg-gradient-solar rounded-2xl p-8 sm:p-12 text-center">
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-primary-foreground mb-3">
                Calculate Your Solar Savings
              </h2>
              <p className="text-primary-foreground/70 max-w-md mx-auto mb-6">
                Find out how much you can save every month with our free solar calculator.
              </p>
              <Link to="/calculator">
                <Button variant="secondary" size="lg" className="gap-2">
                  Open Calculator <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-navy py-20">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-secondary-foreground mb-4">
              Ready to Go Solar?
            </h2>
            <p className="text-secondary-foreground/60 max-w-md mx-auto mb-8">
              Get a free site survey and customized quote for your rooftop solar installation in Jaipur.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="hero" size="lg" className="gap-2">
                  Get Free Quote <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:9145863773">
                <Button variant="outline-light" size="lg">Call +91 91458 63773</Button>
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Index;
